/*
 * Mara League OS — Broadcast Studio (Admin)
 * ------------------------------------------
 * Central page for anyone producing a livestream. Lists every overlay preset
 * with its public URL (copy-to-clipboard), a live iframe preview, and quick
 * links to the Master Config broadcast group.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Tv, Copy, ExternalLink, Radio, Layers, Settings2, Sparkles, Play, Circle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const OVERLAY_PRESETS = [
  {
    key: "scorebug_bottom",
    label: "Scorebug — Bottom center",
    desc: "Standard broadcast lower-third: team scores + clock + service indicator.",
    layoutParam: "scorebug_bottom",
    size: { w: 1920, h: 1080 },
    tags: ["OBS", "vMix", "LED"],
  },
  {
    key: "scorebug_top",
    label: "Scorebug — Top strip",
    desc: "Score anchored at the top; frees the bottom third for lineup graphics.",
    layoutParam: "scorebug_top",
    size: { w: 1920, h: 1080 },
    tags: ["OBS", "vMix"],
  },
  {
    key: "corner_minimal",
    label: "Corner minimal",
    desc: "Small scorebug + big shot-clock — useful during commentary.",
    layoutParam: "corner_minimal",
    size: { w: 1920, h: 1080 },
    tags: ["OBS", "commentary"],
  },
  {
    key: "led_stadium",
    label: "LED stadium",
    desc: "Full-width scorebug + event banners + sponsor rotation for center-hung LEDs.",
    layoutParam: "led_stadium",
    size: { w: 3840, h: 1080 },
    tags: ["LED", "stadium"],
  },
  {
    key: "obs_stream_full",
    label: "Stream · full stack",
    desc: "Everything on: scorebug, shot clock, event banners, lower-third, sponsors.",
    layoutParam: "obs_stream_full",
    size: { w: 1920, h: 1080 },
    tags: ["OBS", "stream"],
  },
  {
    key: "shot_clock_only",
    label: "Shot clock (solo)",
    desc: "Just the shot-clock circle — for auxiliary displays or small tickers.",
    layoutParam: "shot_clock_only",
    size: { w: 240, h: 240 },
    tags: ["aux"],
  },
  {
    key: "sponsors_only",
    label: "Sponsor ticker",
    desc: "Rotating sponsor bar. Configure interval + list under Master Config.",
    layoutParam: "sponsors_only",
    size: { w: 1920, h: 100 },
    tags: ["LED"],
  },
];

export default function BroadcastStudio() {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const [current, setCurrent] = useState({ match_id: null });
  const [customMatchId, setCustomMatchId] = useState("");
  const [chroma, setChroma] = useState(false);
  const [layout, setLayout] = useState(null);   // overrides tenant default
  const [refreshTick, setRefreshTick] = useState(0);

  const publicOrigin = window.location.origin;

  const load = async () => {
    try {
      const { data } = await api.get("/public/broadcast/current");
      setCurrent(data || { match_id: null });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);

  const matchId = customMatchId || current.match_id || "current";
  const chromaParam = chroma ? "&bg=chroma" : "";
  const layoutForcedParam = layout ? `&layout=${layout}` : "";

  const urlFor = (preset) =>
    `${publicOrigin}/overlay/${matchId}?layout=${preset.layoutParam}${chromaParam}${layoutForcedParam ? "" : ""}`.replace(
      /&layout=[^&]+(?=.*&layout=)/,
      ""
    );

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied", { description: url });
    } catch {
      toast.error("Copy failed — long-press to select");
    }
  };

  const preview = (preset) => {
    const url = urlFor(preset);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const brandColor = tenant?.theme_config?.brand_primary || "#DC2626";

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            §10 · Broadcast Overlay Engine
          </div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">
            Broadcast Studio
          </h1>
          <div className="text-xs text-zinc-400 mt-1">
            Drop any of these URLs into OBS as a Browser Source, vMix as a Web input, or point
            a stadium LED controller at them. Overlays auto-follow the live match — no
            reconfiguration between fixtures.
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full animate-pulse"
                 style={{ background: current.match_id ? "#10b981" : "#6b7280" }} />
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              {current.match_id ? (
                <>
                  <span className="text-emerald-400">Live</span>
                  <span className="text-white mx-1">·</span>
                  <span className="text-white font-mono">{current.court}</span>
                  <span className="text-zinc-500 mx-1">·</span>
                  <Badge variant="outline" className="border-white/10 text-white text-[10px]">
                    {current.status}
                  </Badge>
                </>
              ) : "Standby"}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => nav("/admin-config")}
            className="border-white/10 text-zinc-300">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Configure defaults
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card className="p-4 bg-[#11241b] border-white/10 mb-6">
        <div className="flex items-end flex-wrap gap-4">
          <div className="min-w-[280px]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Match target</div>
            <div className="flex items-center gap-2">
              <Input
                value={customMatchId}
                onChange={(e) => setCustomMatchId(e.target.value.trim())}
                placeholder="Auto-follow live match (leave blank)"
                className="bg-[#060d09] border-white/10 text-white font-mono text-xs"
              />
              {customMatchId && (
                <Button size="sm" variant="outline" className="border-white/10 text-zinc-300"
                  onClick={() => setCustomMatchId("")}>
                  Reset
                </Button>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">
              Blank = auto-follow the currently live match for this tenant.
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Background</div>
            <Select value={chroma ? "chroma" : "transparent"} onValueChange={(v) => setChroma(v === "chroma")}>
              <SelectTrigger className="bg-[#060d09] border-white/10 text-white w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a1611] border-white/10 text-white">
                <SelectItem value="transparent">Transparent</SelectItem>
                <SelectItem value="chroma">Magenta chroma-key</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Override layout</div>
            <Select value={layout || "__auto__"} onValueChange={(v) => setLayout(v === "__auto__" ? null : v)}>
              <SelectTrigger className="bg-[#060d09] border-white/10 text-white w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a1611] border-white/10 text-white">
                <SelectItem value="__auto__">Auto (from Master Config)</SelectItem>
                {OVERLAY_PRESETS.map((p) => (
                  <SelectItem key={p.key} value={p.layoutParam}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto">
            <Button variant="outline" onClick={() => setRefreshTick((t) => t + 1)}
              className="border-white/10 text-zinc-300">
              <Radio className="w-4 h-4 mr-1.5" /> Refresh previews
            </Button>
          </div>
        </div>
      </Card>

      {/* Preset grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {OVERLAY_PRESETS.map((p) => {
          const url = urlFor(p);
          return (
            <Card key={p.key} className="p-4 bg-[#11241b] border-white/10 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded flex items-center justify-center shrink-0"
                  style={{ background: brandColor }}>
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-anton text-lg uppercase text-white">{p.label}</h3>
                    {p.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="border-white/10 text-zinc-400 text-[9px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">{p.desc}</div>
                </div>
              </div>

              {/* URL bar */}
              <div className="flex items-center gap-2">
                <code className="flex-1 px-2 py-1.5 rounded bg-black/40 border border-white/5 text-[11px] text-emerald-300 font-mono truncate">
                  {url}
                </code>
                <Button size="sm" variant="outline" className="border-white/10 text-zinc-300 h-8 px-2"
                  onClick={() => copy(url)}
                  data-testid={`copy-${p.key}`}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="border-white/10 text-zinc-300 h-8 px-2"
                  onClick={() => preview(p)}
                  data-testid={`open-${p.key}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </div>

              {/* Live preview */}
              <div className="rounded overflow-hidden border border-white/10 relative bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22><rect width=%2216%22 height=%2216%22 fill=%22%230a0a0a%22/><rect x=%228%22 y=%220%22 width=%228%22 height=%228%22 fill=%22%23111111%22/><rect x=%220%22 y=%228%22 width=%228%22 height=%228%22 fill=%22%23111111%22/></svg>')] aspect-video">
                <iframe
                  key={refreshTick}
                  src={url}
                  title={p.label}
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin"
                />
                {!current.match_id && !customMatchId && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="text-center">
                      <Play className="w-8 h-8 text-white/50 mx-auto mb-1" />
                      <div className="text-white/70 text-xs uppercase tracking-widest">Preview activates during a live match</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] text-zinc-600 pt-1 border-t border-white/5">
                <div className="flex items-center gap-1">
                  <Circle className="w-2 h-2 fill-current" />
                  Target size {p.size.w} × {p.size.h}
                </div>
                <div>Poll interval 500 ms</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="mt-6 p-4 bg-[#11241b] border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <div className="font-anton uppercase text-white text-sm">How to use in OBS</div>
        </div>
        <ol className="text-xs text-zinc-400 list-decimal ml-5 space-y-1">
          <li>In OBS, add a new <span className="text-white">Browser</span> source.</li>
          <li>Paste the URL for the desired preset. Set width/height to match the target size shown on each card (e.g. 1920×1080).</li>
          <li>Leave <span className="text-white">Refresh browser when scene becomes active</span> unchecked — the overlay is self-updating.</li>
          <li>Layer as needed. For chroma-key workflows, use the <span className="text-white">Magenta chroma-key</span> background and add a Color Key filter on <span className="text-white">Magenta</span>.</li>
          <li>Point-of-view changes automatically as matches start / finish — the overlay follows the tenant&apos;s live match.</li>
        </ol>
      </Card>

      <div className="mt-6 flex items-center gap-3 text-xs">
        <Tv className="w-4 h-4 text-zinc-500" />
        <div className="text-zinc-500">
          Telemetry endpoint:{" "}
          <code className="text-emerald-300 font-mono">GET /api/public/broadcast/telemetry/{"{match_id}"}</code>
        </div>
      </div>
    </div>
  );
}
